import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type PolicyMatchMode = 'all' | 'any';
export type PolicyAssetType = 'html-pages' | 'documents' | 'all-assets';
export type PolicyViolationLabel = 'unwanted' | 'required' | 'matches';
export type PolicyPriority = 'low' | 'medium' | 'high';
export type PolicyDomainScope = 'all-domains' | 'selected-domains';
export type PolicyStatus = 'draft' | 'active' | 'archived';

/**
 * The subject a rule group is evaluated against: `page` groups test page-level
 * facts, entity scopes evaluate all conditions of the group against the *same*
 * item (image, meta tag, loaded file), quantified over the collection.
 */
export type PolicyRuleScope = 'page' | PolicyEntityScope;
export type PolicyEntityScope = 'image' | 'meta-tag' | 'file';

/** `any`: at least one item matches; `every`: all items do (vacuously true when none). */
export type PolicyRuleQuantifier = 'any' | 'every';

export interface PolicyRuleCondition {
  field: string;
  operator: string;
  value: string;
}

/** A page-level group: each condition is a standalone page check. */
export interface PolicyPageRuleGroup {
  scope: 'page';
  quantifier?: never;
  matchMode: PolicyMatchMode;
  rules: PolicyRuleCondition[];
}

/** An entity-scoped group: conditions share one item, fields are the scope's attributes. */
export interface PolicyScopedRuleGroup {
  scope: PolicyEntityScope;
  quantifier: PolicyRuleQuantifier;
  matchMode: PolicyMatchMode;
  rules: PolicyRuleCondition[];
}

export type PolicyRuleGroup = PolicyPageRuleGroup | PolicyScopedRuleGroup;

export interface PolicyRule {
  matchMode: PolicyMatchMode;
  groups: PolicyRuleGroup[];
}

/** Request body accepted by `POST /policies`. */
export interface CreatePolicyBody {
  name: string;
  description?: string | null;
  assetType: PolicyAssetType;
  violationLabel: PolicyViolationLabel;
  priority: PolicyPriority;
  /** Optional organisation team this policy belongs to. */
  teamId: number | null;
  domainScope: PolicyDomainScope;
  /** auditEntry ids; only used when domainScope === 'selected-domains'. */
  auditEntryIds?: number[];
  rule: PolicyRule;
  status: PolicyStatus;
}

/** Shape returned by the policies endpoints. */
export interface PolicyResponse {
  id: number;
  organisationId: number;
  name: string;
  description: string | null;
  assetType: PolicyAssetType;
  violationLabel: PolicyViolationLabel;
  priority: PolicyPriority;
  teamId: number | null;
  domainScope: PolicyDomainScope;
  rule: PolicyRule;
  status: PolicyStatus;
  auditEntryIds: number[];
  createdAt: string | null;
  updatedAt: string | null;
  /** When the policy's domains were last scanned; only set on GET responses. */
  lastScanAt?: string | null;
  /** Current number of open issues found by this policy; only set on GET. */
  openIssueCount?: number;
}

/** Create a custom governance policy (used by the /policies/new form). */
export function useCreatePolicy() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body: CreatePolicyBody) =>
      fetchWrapper.post<PolicyResponse>(api.policies, body),
    onSuccess: async (policy) => {
      await queryClient.invalidateQueries({ queryKey: [api.policies] });
      notification.success(t('policyCreatedSuccess'));
      return policy;
    },
  });
}
